import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Loader2, ZoomIn, ZoomOut, Maximize2, AlertTriangle, Star, Paperclip,
  User, FileText, ChevronDown, X, Info, MapPin, StickyNote,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { api } from '../App';
import { toast } from 'sonner';
import './FlowTreeView.css';

// ---- date parsing for chronological ordering of transition documents ----
const parseDate = (s) => {
  if (!s) return 0;
  const str = String(s).trim();
  let m = str.match(/(\d{1,2})[-./](\d{1,2})[-./](\d{2,4})/);
  if (m) {
    let [, d, mo, y] = m;
    if (y.length === 2) y = '20' + y;
    const t = new Date(+y, +mo - 1, +d).getTime();
    return isNaN(t) ? 0 : t;
  }
  m = str.match(/\b(\d{4})\b/);
  if (m) return new Date(+m[1], 0, 1).getTime() || 0;
  const t = Date.parse(str);
  return isNaN(t) ? 0 : t;
};

const surveyLine = (s) =>
  [s.sy_no, [s.extent, s.unit].filter(Boolean).join(' ')].filter(Boolean).join(' – ');

// =====================================================================
const EventBox = ({ doc, opt, onSelect }) => {
  const missing = !opt;
  const desc = (opt?.description || '').trim();
  const shortDesc = desc.length > 90 ? desc.slice(0, 90) + '…' : desc;
  return (
    <button
      className="oft-box oft-event ownership"
      onClick={() => onSelect({ type: 'doc', doc, opt })}
      data-testid={`oft-event-${doc.entry_id}`}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        <FileText size={12} className="text-fuchsia-700 shrink-0" />
        <span className="text-[13px] font-bold text-fuchsia-900 leading-tight">
          {missing ? 'Document unavailable' : opt.document_type}
        </span>
        <span className="ml-auto flex items-center gap-0.5 text-[9px] font-bold text-amber-700">
          <Star size={9} className="fill-amber-500 text-amber-500" /> MAIN
        </span>
      </div>
      {missing ? (
        <div className="text-[11px] text-amber-700">Attached document no longer exists — Needs Review</div>
      ) : (
        <div className="text-[11px] text-slate-600 space-y-0.5">
          {opt.serial_number != null && <div><span className="text-slate-400">Sl.</span> #{opt.serial_number}</div>}
          {opt.document_number && <div>Doc No: {opt.document_number}</div>}
          {opt.date && <div>Date: {opt.date}</div>}
          {shortDesc && <div className="text-slate-500 italic">{shortDesc}</div>}
        </div>
      )}
    </button>
  );
};

const ReviewBox = () => (
  <div className="oft-box oft-review" data-testid="oft-review-box">
    <div className="flex items-center gap-1.5 text-[12px] font-semibold">
      <AlertTriangle size={13} /> Needs Review
    </div>
    <div className="text-[11px] mt-0.5">No ownership-changing (Main) document identified for this transition.</div>
  </div>
);

const OwnerBox = ({ node, docMap, onSelect, isRoot }) => {
  const surveys = (node.surveys || []).filter((s) => s.sy_no || s.extent);
  const supporting = (node.documents || []).filter((d) => d.role === 'supporting');
  return (
    <button
      className="oft-box oft-owner"
      onClick={() => onSelect({ type: 'owner', node })}
      data-testid={`oft-owner-${node.id}`}
    >
      <div className="flex items-center gap-1.5">
        <User size={13} className="text-[#1e3a5f] shrink-0" />
        <span className="oft-owner-badge">{isRoot ? 'Original Owner' : 'Owner'}</span>
      </div>
      <div className="text-[14px] font-bold text-[#1e3a5f] leading-tight mt-0.5">
        {node.owner_name || <span className="italic text-slate-400 font-normal">Unnamed owner</span>}
      </div>
      {surveys.length > 0 && (
        <div className="text-[11px] text-slate-600 mt-1">
          {surveys.map((s, i) => (
            <div key={i} className="flex items-center gap-1">
              <MapPin size={10} className="text-slate-400 shrink-0" /> {surveyLine(s)}
            </div>
          ))}
        </div>
      )}
      {node.remarks && (
        <div className="text-[11px] text-slate-500 italic mt-1 flex items-start gap-1">
          <StickyNote size={10} className="mt-0.5 shrink-0" /> {node.remarks}
        </div>
      )}
      {supporting.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {supporting.map((d, i) => {
            const o = docMap[d.entry_id];
            return (
              <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
                <Paperclip size={9} />
                {o ? `#${o.serial_number} ${o.document_type}` : 'Missing doc'}
              </span>
            );
          })}
        </div>
      )}
    </button>
  );
};

const Down = () => <div className="oft-down" />;

// Recursive node — rendered via createElement to avoid self-referential JSX.
const FlowNode = ({ node, isRoot, suppressIncoming, docMap, onSelect }) => {
  const mainOf = (n) => (n.documents || []).filter((d) => d.role === 'main');
  const sortByDate = (arr) =>
    [...arr].sort((a, b) => parseDate(docMap[a.entry_id]?.date) - parseDate(docMap[b.entry_id]?.date));

  const incoming = suppressIncoming ? [] : sortByDate(mainOf(node));
  const needsReview = !isRoot && !suppressIncoming && incoming.length === 0;
  const children = node.children || [];

  // Shared-transition lift: if every child has exactly one identical Main doc,
  // render that ownership event once above the branch fan-out (e.g. Partition).
  const childMain = children.map((c) => mainOf(c).map((d) => d.entry_id));
  const lift =
    children.length >= 2 &&
    childMain.every((ids) => ids.length === 1) &&
    childMain.every((ids) => ids[0] === childMain[0][0]);
  const liftedDoc = lift ? mainOf(children[0])[0] : null;

  return (
    <div className="oft-node">
      {incoming.map((d, i) => (
        <React.Fragment key={d.entry_id + i}>
          <EventBox doc={d} opt={docMap[d.entry_id]} onSelect={onSelect} />
          <Down />
        </React.Fragment>
      ))}
      {needsReview && (<><ReviewBox /><Down /></>)}

      <OwnerBox node={node} docMap={docMap} onSelect={onSelect} isRoot={isRoot} />

      {children.length > 0 && <Down />}
      {liftedDoc && (<><EventBox doc={liftedDoc} opt={docMap[liftedDoc.entry_id]} onSelect={onSelect} /><Down /></>)}

      {children.length > 0 && (
        <div className="oft-children">
          {children.map((c) => (
            <div className="oft-child" key={c.id}>
              {React.createElement(FlowNode, {
                node: c,
                isRoot: false,
                suppressIncoming: lift,
                docMap,
                onSelect,
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =====================================================================
const FlowTreeView = ({ caseId, heightClass = 'h-[70vh]' }) => {
  const [tree, setTree] = useState([]);
  const [docList, setDocList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState(null);

  const docMap = useMemo(() => {
    const m = {};
    docList.forEach((d) => { m[d.entry_id] = d; });
    return m;
  }, [docList]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [flowRes, docsRes] = await Promise.all([
        api.get(`/cases/${caseId}/manual-ownership-flow`),
        api.get(`/cases/${caseId}/flow-document-options`),
      ]);
      setTree(flowRes.data.tree || []);
      setDocList(Array.isArray(docsRes.data) ? docsRes.data : []);
    } catch (e) {
      toast.error('Failed to load ownership flow');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const onSelect = (sel) => setSelected(sel);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500" data-testid="oft-loading">
        <Loader2 className="animate-spin mr-2" size={20} /> Loading tree…
      </div>
    );
  }

  if (!tree || tree.length === 0) {
    return (
      <div className="text-center py-20 border-2 border-dashed rounded-lg bg-white" data-testid="oft-empty">
        <FileText size={40} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-600 font-medium">No ownership flow built yet</p>
        <p className="text-slate-400 text-sm mt-1">Switch to the Builder to add owners and attach documents.</p>
      </div>
    );
  }

  return (
    <div data-testid="flow-tree-view">
      {/* Controls + legend */}
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} data-testid="oft-zoom-out"><ZoomOut size={15} /></Button>
          <span className="text-xs text-slate-500 w-10 text-center" data-testid="oft-zoom-level">{Math.round(zoom * 100)}%</span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))} data-testid="oft-zoom-in"><ZoomIn size={15} /></Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setZoom(1)} data-testid="oft-zoom-reset" title="Reset zoom"><Maximize2 size={14} /></Button>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border-2 border-[#1e3a5f]" /> Owner</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border-[1.5px] border-fuchsia-600 bg-fuchsia-50" /> Ownership document / event</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border-[1.5px] border-dashed border-amber-500 bg-amber-50" /> Needs review</span>
          <span className="flex items-center gap-1"><Paperclip size={11} /> Supporting doc</span>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-slate-400 ml-auto">
          <Info size={12} /> Each owner's Main document is shown as the event that transferred title to them.
        </div>
      </div>

      {/* Tree */}
      <div className={`oft-scroll ${heightClass}`} data-testid="oft-canvas">
        <div className="oft-scale" style={{ transform: `scale(${zoom})` }}>
          <div className="oft-forest">
            {tree.map((root) => (
              <div key={root.id}>
                {React.createElement(FlowNode, {
                  node: root, isRoot: true, suppressIncoming: false, docMap, onSelect,
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="oft-detail-dialog">
          <DialogDescription className="sr-only">Ownership flow node details</DialogDescription>
          {selected?.type === 'owner' && (
            <OwnerDetail node={selected.node} docMap={docMap} />
          )}
          {selected?.type === 'doc' && (
            <DocDetail doc={selected.doc} opt={selected.opt} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const OwnerDetail = ({ node, docMap }) => {
  const surveys = (node.surveys || []).filter((s) => s.sy_no || s.extent);
  const docs = node.documents || [];
  const mains = docs.filter((d) => d.role === 'main');
  const sups = docs.filter((d) => d.role === 'supporting');
  const DocRow = ({ d }) => {
    const o = docMap[d.entry_id];
    return (
      <div className="border rounded-md p-2 text-sm">
        <div className="flex items-center gap-2">
          {d.role === 'main'
            ? <span className="text-[10px] font-bold text-amber-700 flex items-center gap-0.5"><Star size={10} className="fill-amber-500 text-amber-500" />MAIN</span>
            : <span className="text-[10px] font-bold text-slate-500 flex items-center gap-0.5"><Paperclip size={10} />SUPPORTING</span>}
          <span className="font-semibold text-slate-800">{o ? `#${o.serial_number} ${o.document_type}` : 'Document no longer exists'}</span>
        </div>
        {o?.document_number && <div className="text-xs text-slate-500">Doc No: {o.document_number}</div>}
        {o?.date && <div className="text-xs text-slate-500">Date: {o.date}</div>}
        {o?.description && <div className="text-xs text-slate-500 italic mt-0.5">{o.description}</div>}
        {d.remarks && <div className="text-xs text-slate-600 mt-1">Remark: {d.remarks}</div>}
      </div>
    );
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><User size={18} className="text-[#1e3a5f]" /> {node.owner_name || 'Unnamed owner'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        {surveys.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Survey Numbers</div>
            {surveys.map((s, i) => <div key={i} className="text-slate-700">{surveyLine(s)}</div>)}
          </div>
        )}
        {node.remarks && (
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Remarks</div>
            <div className="text-slate-700">{node.remarks}</div>
          </div>
        )}
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1">Ownership-changing documents (Main)</div>
          {mains.length ? <div className="space-y-1.5">{mains.map((d, i) => <DocRow key={i} d={d} />)}</div>
            : <div className="text-slate-400 italic text-xs">None attached.</div>}
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1">Supporting documents</div>
          {sups.length ? <div className="space-y-1.5">{sups.map((d, i) => <DocRow key={i} d={d} />)}</div>
            : <div className="text-slate-400 italic text-xs">None attached.</div>}
        </div>
      </div>
    </>
  );
};

const DocDetail = ({ doc, opt }) => (
  <>
    <DialogHeader>
      <DialogTitle className="flex items-center gap-2"><FileText size={18} className="text-fuchsia-700" /> {opt ? opt.document_type : 'Document unavailable'}</DialogTitle>
    </DialogHeader>
    <div className="space-y-2 text-sm">
      {!opt && <div className="text-amber-700 text-sm">This attached document no longer exists in the case — Needs Review.</div>}
      {opt && (
        <div className="space-y-1 text-slate-700">
          <div><span className="text-slate-400 text-xs">Serial No.</span> #{opt.serial_number}</div>
          {opt.category && <div><span className="text-slate-400 text-xs">Category</span> {opt.category}</div>}
          {opt.document_number && <div><span className="text-slate-400 text-xs">Document No.</span> {opt.document_number}</div>}
          {opt.date && <div><span className="text-slate-400 text-xs">Date</span> {opt.date}</div>}
          {opt.description && <div className="text-slate-600 italic">{opt.description}</div>}
        </div>
      )}
      <div className="text-[10px] font-bold text-amber-700 flex items-center gap-0.5"><Star size={10} className="fill-amber-500 text-amber-500" />MAIN (ownership-changing event)</div>
      {doc.remarks && <div className="text-slate-700 text-sm pt-1 border-t"><span className="text-slate-400 text-xs">Remark</span><br />{doc.remarks}</div>}
    </div>
  </>
);

export default FlowTreeView;
